import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useFormContext, Controller, ControllerRenderProps, ControllerFieldState, UseFormStateReturn, FieldPath } from 'react-hook-form';
import { Input, Toggle, Button } from '../../common';
import { PresetType, PresetCreateRequest, TradeSetCreateRequest } from '../../../types/preset';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../config';

interface WebhookConfigProps {
  presetType: PresetType;
  hasExistingSecret?: boolean; // Indicate if editing preset has an existing secret
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.5rem 0;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const InfoText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;

const CodeBlock = styled.pre`
  background-color: #1f2937;
  color: #e5e7eb;
  padding: 1rem;
  border-radius: 0.375rem;
  font-family: monospace;
  font-size: 0.875rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: #4b5563;
  color: white;
  border: none;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #374151;
  }
`;

const CodeBlockContainer = styled.div`
  position: relative;
  margin-top: 1rem;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0;
`;

type FormDataType = (PresetCreateRequest | TradeSetCreateRequest) & {
  allow_override?: boolean;
  webhook_secret?: string;
};

type ControllerRenderParams<TName extends FieldPath<FormDataType>> = {
  field: ControllerRenderProps<FormDataType, TName>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<FormDataType>;
};

const WebhookConfig: React.FC<WebhookConfigProps> = ({ presetType, hasExistingSecret = false }) => {
  const { control, watch, formState: { errors }, setValue } = useFormContext<FormDataType>();

  const isWebhookEnabled = watch('webhook_enabled');
  const watchedWebhookId = watch('webhook_id');
  const watchedName = watch('name');
  const watchedSecret = watch('webhook_secret');
  
  // Watch form values for dynamic example payload
  const watchedAmount = watch('amount');
  const watchedAmountType = watch('amount_type');
  const watchedLeverage = watch('leverage');
  
  useEffect(() => {
    if (isWebhookEnabled && !watchedWebhookId) {
      const safeName = watchedName?.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'preset';
      const defaultId = `${safeName}_${presetType}_${uuidv4().substring(0, 8)}`;
      setValue('webhook_id', defaultId, { shouldValidate: false, shouldDirty: true });
    }
  }, [isWebhookEnabled, watchedWebhookId, watchedName, presetType, setValue]);

  const examplePayload = {
    webhook_id: watchedWebhookId || 'YOUR_WEBHOOK_ID',
    passphrase: watchedSecret || 'YOUR_SECRET_PASSPHRASE',
    amount: watchedAmount ? Number(watchedAmount) : 100, 
    amount_type: watchedAmountType || "quote",
    leverage: watchedLeverage ? Number(watchedLeverage) : 5
  };
  
  const [payloadCopied, setPayloadCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const handleCopy = (textToCopy: string, setCopiedState: React.Dispatch<React.SetStateAction<boolean>>) => {
    navigator.clipboard.writeText(textToCopy);
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 2000);
  };

  // Use centralized webhook configuration
  const fullWebhookUrl = `${config.webhookBaseUrl}/api/v1/webhook/trade`;

  return (
    <StepContainer>
      <FormSection>
        <SectionTitle>Webhook Configuration</SectionTitle>
        
        <InputGroup>
          <Label>Enable Webhook Trigger</Label>
          <Controller
            name="webhook_enabled"
            control={control}
            defaultValue={false}
            render={({ field }: ControllerRenderParams<"webhook_enabled">) => (
              <>
                <Toggle
                  options={[
                    { value: 'false', label: 'Disabled' },
                    { value: 'true', label: 'Enabled' }
                  ]}
                  value={field.value ? 'true' : 'false'}
                  onChange={(value) => field.onChange(value === 'true')}
                />
                <InfoText>
                  Allow this configuration to be executed via TradingView webhook alerts.
                </InfoText>
              </>
            )}
          />
        </InputGroup>
        
        {isWebhookEnabled && (
          <>
            <InputGroup>
              <Label htmlFor="webhook_id">Webhook ID</Label>
              <Controller
                name="webhook_id"
                control={control}
                rules={{ 
                  required: presetType === 'preset' ? 'Webhook ID is required for preset webhooks' : false,
                  pattern: { value: /^[a-zA-Z0-9_-]+$/, message: 'Webhook ID can only contain letters, numbers, underscores, and hyphens'}
                }}
                render={({ field }: ControllerRenderParams<"webhook_id">) => (
                  <Input
                    id="webhook_id"
                    placeholder={`Enter a unique ID (e.g., long_${presetType}_btc)`}
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
              <InfoText>
                This ID must be included in your TradingView alert payload ('webhook_id' field).
              </InfoText>
              {errors.webhook_id && <ErrorText>{errors.webhook_id.message}</ErrorText>}
            </InputGroup>
            
            <InputGroup>
              <Label htmlFor="webhook_secret">Webhook Secret / Passphrase</Label>
              <Controller
                name="webhook_secret"
                control={control}
                render={({ field }: ControllerRenderParams<"webhook_secret">) => (
                  <Input
                    id="webhook_secret"
                    type="password"
                    placeholder={hasExistingSecret ? "Leave empty to keep existing secret, or enter new secret" : "Enter a secure secret passphrase"}
                    {...field}
                    value={field.value || ''}
                  />
                )}
              />
              <InfoText>
                {hasExistingSecret 
                  ? "A secret is already configured. Leave empty to keep existing, or enter a new one to replace it."
                  : "Required for authenticating webhook requests. Keep this secure. Include as 'passphrase' field in your JSON payload."
                }
              </InfoText>
              {errors.webhook_secret && <ErrorText>{errors.webhook_secret.message}</ErrorText>}
            </InputGroup>
            
            <InputGroup>
              <Label>Allow Parameter Overrides</Label>
              <Controller
                name="allow_override"
                control={control}
                defaultValue={true}
                render={({ field }: ControllerRenderParams<"allow_override">) => (
                  <>
                    <Toggle
                      options={[
                        { value: 'false', label: 'Disabled' },
                        { value: 'true', label: 'Enabled' }
                      ]}
                      value={field.value ? 'true' : 'false'}
                      onChange={(value) => field.onChange(value === 'true')}
                    />
                    <InfoText>
                      Allow parameters like amount, leverage, etc., sent in the webhook payload to override the saved preset settings.
                    </InfoText>
                  </>
                )}
              />
              {errors.allow_override && <ErrorText>{errors.allow_override.message}</ErrorText>}
            </InputGroup>
            
            <SectionTitle>TradingView Setup</SectionTitle>
            <InfoText>
              Use the following URL and Payload structure in your TradingView Alert settings.
            </InfoText>
            
            <InputGroup>
              <Label>Webhook URL</Label>
              <CodeBlockContainer>
                <CodeBlock>{fullWebhookUrl}</CodeBlock>
                <CopyButton onClick={() => handleCopy(fullWebhookUrl, setUrlCopied)}>
                  {urlCopied ? 'Copied!' : 'Copy URL'}
                </CopyButton>
              </CodeBlockContainer>
            </InputGroup>
            
            <InputGroup>
              <Label>Example Payload (Alert Message Body)</Label>
              <CodeBlockContainer>
                <CodeBlock>
                  {JSON.stringify({
                    webhook_id: watchedWebhookId || 'YOUR_WEBHOOK_ID',
                    passphrase: "YOUR_SECRET_PASSPHRASE",
                    // Optional override parameters
                    amount: watchedAmount ? Number(watchedAmount) : 100,
                    amount_type: watchedAmountType || "quote",
                    leverage: watchedLeverage ? Number(watchedLeverage) : 5,
                  }, null, 2)}
                </CodeBlock>
                <CopyButton onClick={() => handleCopy(JSON.stringify(examplePayload, null, 2), setPayloadCopied)}>
                  {payloadCopied ? 'Copied!' : 'Copy Payload'}
                </CopyButton>
              </CodeBlockContainer>
              <InfoText>
                Customize this payload in TradingView alerts. The webhook_id and passphrase are required.
                Other fields are optional and will override preset values if "Allow Parameter Overrides" is enabled.
              </InfoText>
            </InputGroup>
          </>
        )}
      </FormSection>
    </StepContainer>
  );
};

export default WebhookConfig; 